/**
 * HOF-216 — operator-facing copy + line lookup for the manual line unlink.
 *
 * The unlink itself has existed since #1087 and has only ever been reachable
 * by selecting the edge and pressing Backspace/Delete. #1344 discovered that
 * operators "clicked, saw no change, and never trusted Backspace/Delete to
 * unlink", fixed the SELECTION feedback and left the TRIGGER keyboard-only;
 * on 2026-08-31 the founder rediscovered it as missing while reconciling
 * Wiesenhof. On a trackpad or touch device the path does not exist at all.
 *
 * Making an existing mutation *reachable* raises its call rate, and this one
 * rewrites ``gutschrift_match_group.fact_value`` on a paying tenant's twin
 * under impersonation (an unlink that empties the only pair flips ``matched``
 * → ``partial``, ``match_groups.py:521``). The confirmation naming both lines
 * and both quantities IS the mitigation — so the copy is built by a pure
 * function here rather than assembled inline, and "names all four parts" is a
 * unit assertion instead of a screenshot.
 *
 * Strings are hardcoded de-DE, matching this surface's existing convention
 * (``'Beleg markiert.'``, ``'← Übersicht'`` in ``GutschriftCheckPage.tsx``) —
 * there is no i18n key for the Gutschrift-Check screen and a half-translated
 * one would be worse than a consistently German one.
 */
import type { MatchGroup } from '../../../hooks/useMatchGroups';

import type { LineSide } from './lineLinkValidation';

/** The label on the on-edge control and the context-menu action. Antonio's own wording. */
export const UNLINK_ACTION_LABEL = 'Verknüpfung lösen';
/**
 * Tooltip on the on-edge control. It NAMES the keyboard shortcut rather than
 * replacing it (AC 6): the shortcut has always worked, it was only ever
 * undiscoverable, and a founder batch-correcting a month wants it.
 */
export const UNLINK_SHORTCUT_HINT = 'Verknüpfung lösen (Entf oder Rücktaste)';
export const UNLINK_CONFIRM_TITLE = 'Verknüpfung lösen?';
export const UNLINK_CONFIRM_CANCEL = 'Abbrechen';
/** The consequence AC 3 requires the confirmation to name. */
export const UNLINK_REEVALUATION_NOTE = 'Die Gruppe wird danach neu bewertet.';

const UNNAMED_LINE = 'ohne Bezeichnung';
const UNKNOWN_QUANTITY = 'Menge unbekannt';

/**
 * What the confirmation needs to say about one line. A superset of
 * ``LineSideEntry`` (which carries only side + group, being built for the
 * connection validators) — kept as its own map so the validation contract
 * pinned by ``lineLinkValidation.test.ts`` does not move.
 */
export interface UnlinkLineDescriptor {
  lineId: string;
  side: LineSide;
  groupId: string;
  productName: string;
  quantity: number | null;
  quantityUnit: string | null;
}

export type LineDescriptorMap = ReadonlyMap<string, UnlinkLineDescriptor>;

/** ``lineId → descriptor`` over both sides of every rendered group. */
export function buildLineDescriptorMap(groups: readonly MatchGroup[]): LineDescriptorMap {
  const map = new Map<string, UnlinkLineDescriptor>();
  for (const g of groups) {
    for (const dl of g.deliveryLines) {
      map.set(dl.lineId, {
        lineId: dl.lineId,
        side: 'delivery',
        groupId: g.id,
        productName: dl.productName,
        quantity: dl.quantity,
        quantityUnit: dl.quantityUnit,
      });
    }
    for (const sl of g.settlementLines) {
      map.set(sl.lineId, {
        lineId: sl.lineId,
        side: 'settlement',
        groupId: g.id,
        productName: sl.productName,
        quantity: sl.quantity,
        quantityUnit: sl.quantityUnit,
      });
    }
  }
  return map;
}

const QUANTITY_FORMAT = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });

/**
 * ``„Rhabarber" (120 kg)``.
 *
 * A missing quantity is spelled out rather than dropped: an unquantified or
 * unpriced Lieferschein line is exactly HOF-215's defect class, so "this pair
 * has no quantity" is information the operator is deciding on, not noise.
 * Tolerates ``undefined`` because a descriptor lookup can miss — a canvas
 * refetch can land between the right-click and the confirm — and a crash
 * there would be strictly worse than a vaguer sentence.
 */
export function formatUnlinkLine(descriptor?: UnlinkLineDescriptor | null): string {
  const name = descriptor?.productName?.trim() || UNNAMED_LINE;
  const quantity =
    descriptor && descriptor.quantity !== null && Number.isFinite(descriptor.quantity)
      ? [QUANTITY_FORMAT.format(descriptor.quantity), descriptor.quantityUnit?.trim() || null]
          .filter((part): part is string => !!part)
          .join(' ')
      : UNKNOWN_QUANTITY;
  return `„${name}" (${quantity})`;
}

/**
 * The confirmation body. Names all four things AC 3 demands: the Lieferschein
 * line, the Gutschrift line, both quantities, and the re-evaluation.
 */
export function buildUnlinkConfirmationMessage(params: {
  delivery?: UnlinkLineDescriptor | null;
  settlement?: UnlinkLineDescriptor | null;
}): string {
  return (
    `Die Lieferschein-Zeile ${formatUnlinkLine(params.delivery)} ` +
    `wird von der Gutschrift-Zeile ${formatUnlinkLine(params.settlement)} getrennt. ` +
    UNLINK_REEVALUATION_NOTE
  );
}
